import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CreditCard, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";

export const StripeConnectCard = () => {
  const [params] = useSearchParams();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/connect/status").then(({ data }) => setSt(data)).catch(() => setSt({ connected: false }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("stripe") === "return") toast.success("Compte Stripe connecté — vérification en cours.");
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const dashboard = async () => {
    try {
      const { data } = await api.post("/connect/dashboard-link");
      if (data.url) window.open(data.url, "_blank");
      else toast.info("Tableau de bord Stripe indisponible en mode démonstration.");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  if (!st) return null;
  const active = st.connected && st.charges_enabled;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="stripe-connect-card">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? "bg-emerald-50 text-emerald-700" : "bg-[#FF7A5C]/10 text-[#FF7A5C]"}`}><CreditCard className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="font-display font-bold text-deep flex items-center gap-2">
            Encaissement CB des téléconsultations
            {active && <span className="text-[10px] font-extrabold uppercase tracking-wide bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="stripe-connect-active">Actif</span>}
            {st.mock && <span className="text-[10px] font-bold text-amber-700" data-testid="stripe-connect-mock">mode démonstration</span>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Modèle Doctolib : le patient enregistre sa carte à la réservation, il est débité de <b>{st.fee_eur} €</b> automatiquement à la fin de la téléconsultation.
            Les fonds arrivent <b>directement sur votre compte bancaire</b> via votre compte Stripe Express — Doctobook ne prélève aucune commission.
          </p>
          {active ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Paiements activés{st.payouts_enabled ? " · virements activés" : ""}</span>
              <Button size="sm" variant="outline" onClick={dashboard} data-testid="stripe-dashboard-btn">Tableau de bord Stripe <ExternalLink className="h-3 w-3 ml-1" /></Button>
            </div>
          ) : st.connected ? (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Dossier incomplet — {st.requirements_due?.length || 0} information(s) à compléter</span>
              <Button size="sm" onClick={connect} disabled={busy} className="btn-primary" data-testid="stripe-connect-btn">Compléter mon dossier</Button>
            </div>
          ) : (
            <div className="mt-3">
              <Button onClick={connect} disabled={busy} className="btn-coral font-bold" data-testid="stripe-connect-btn">{busy ? "Redirection…" : "Connecter mon compte Stripe (3 min)"}</Button>
              <div className="text-[11px] text-slate-400 mt-2">Frais Stripe à votre charge ≈ 1,5 % + 0,25 € par paiement CB européen · aucun abonnement.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
